// src/context/ToastContext.jsx

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  XCircle,
} from "lucide-react";

import "../sass/components/toast.scss";

const ToastContext = createContext(null);

const TOAST_ICONS = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

const TOAST_TITLES = {
  success: "Tudo certo",
  warning: "Atenção",
  error: "Não foi possível concluir",
  info: "Informação",
};

const DEFAULT_DURATION = {
  success: 4200,
  warning: 6200,
  error: 7200,
  info: 5200,
};

function createToastId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeToastPayload(type, messageOrOptions, options = {}) {
  const base =
    typeof messageOrOptions === "object" && messageOrOptions !== null
      ? messageOrOptions
      : {
          message: String(messageOrOptions || ""),
          ...options,
        };

  return {
    id: createToastId(),
    type,
    title: base.title || TOAST_TITLES[type],
    message: base.message || base.text || "",
    details: Array.isArray(base.details) ? base.details.filter(Boolean) : [],
    actionLabel: base.actionLabel || "",
    onAction: typeof base.onAction === "function" ? base.onAction : null,
    duration:
      base.duration === 0
        ? 0
        : Number(base.duration || DEFAULT_DURATION[type] || 5200),
  };
}

function ToastItem({ toast, onDismiss }) {
  const Icon = TOAST_ICONS[toast.type] || Info;
  const timerRef = useRef(null);
  const remainingRef = useRef(toast.duration);
  const startedAtRef = useRef(null);

  const startTimer = useCallback(() => {
    if (!toast.duration || remainingRef.current <= 0) {
      return;
    }

    startedAtRef.current = Date.now();

    timerRef.current = window.setTimeout(() => {
      onDismiss(toast.id);
    }, remainingRef.current);
  }, [onDismiss, toast.duration, toast.id]);

  const pauseTimer = useCallback(() => {
    if (!timerRef.current) {
      return;
    }

    window.clearTimeout(timerRef.current);
    timerRef.current = null;

    if (startedAtRef.current) {
      remainingRef.current = Math.max(
        0,
        remainingRef.current - (Date.now() - startedAtRef.current)
      );
    }
  }, []);

  React.useEffect(() => {
    startTimer();

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [startTimer]);

  const handleAction = () => {
    toast.onAction?.();
    onDismiss(toast.id);
  };

  return (
    <article
      className={`psy-toast psy-toast--${toast.type}`}
      role={toast.type === "error" ? "alert" : "status"}
      aria-live={toast.type === "error" ? "assertive" : "polite"}
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
    >
      <div className="psy-toast__accent" aria-hidden="true" />

      <div className="psy-toast__icon" aria-hidden="true">
        <Icon size={19} strokeWidth={2.2} />
      </div>

      <div className="psy-toast__content">
        <strong>{toast.title}</strong>

        {toast.message ? <p>{toast.message}</p> : null}

        {toast.details.length ? (
          <div className="psy-toast__details">
            {toast.details.map((detail, index) => (
              <span key={`${toast.id}-detail-${index}`}>{detail}</span>
            ))}
          </div>
        ) : null}

        {toast.actionLabel && toast.onAction ? (
          <button
            type="button"
            className="psy-toast__action"
            onClick={handleAction}
          >
            {toast.actionLabel}
          </button>
        ) : null}
      </div>

      <button
        type="button"
        className="psy-toast__close"
        onClick={() => onDismiss(toast.id)}
        aria-label="Fechar notificação"
      >
        <X size={17} />
      </button>

      {toast.duration > 0 ? (
        <span
          className="psy-toast__progress"
          style={{ "--psy-toast-duration": `${toast.duration}ms` }}
          aria-hidden="true"
        />
      ) : null}
    </article>
  );
}

function ToastViewport({ toasts, onDismiss }) {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="psy-toast-viewport" aria-label="Notificações do sistema">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((toastId) => {
    setToasts((current) => current.filter((toast) => toast.id !== toastId));
  }, []);

  const clear = useCallback(() => {
    setToasts([]);
  }, []);

  const push = useCallback((type, messageOrOptions, options) => {
    const toast = normalizeToastPayload(type, messageOrOptions, options);

    setToasts((current) => {
      const next = [...current, toast];
      return next.slice(Math.max(0, next.length - 4));
    });

    return toast.id;
  }, []);

  const toast = useMemo(
    () => ({
      success: (messageOrOptions, options) =>
        push("success", messageOrOptions, options),
      warning: (messageOrOptions, options) =>
        push("warning", messageOrOptions, options),
      error: (messageOrOptions, options) =>
        push("error", messageOrOptions, options),
      info: (messageOrOptions, options) =>
        push("info", messageOrOptions, options),
      dismiss,
      clear,
    }),
    [clear, dismiss, push]
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast deve ser utilizado dentro de ToastProvider.");
  }

  return context;
}
